import React, { FC, useEffect, useState, useCallback, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';
// selectors
import {
  useConfigSelectors,
  useFilterDataSelectors,
  useLanguageSelectors,
  useRolesSelectors,
} from 'src/app/reducers';
import { useDataSelectors } from 'src/@infringements/reducers/dataReducer';
import { InfringementDispatchContext } from 'src/@infringements/providers/InfringementsStateProvider';
// actions
import { selectRow } from 'src/@infringements/actions';
import { dataActionTypes } from 'src/@infringements/newActionTypes';
// components
import { Table, Icons } from '@ems/client-design-system';
// function
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
import { formatHeaders, formatInfringementData } from 'src/@infringements/functions';
import { UPDATE_INFRINGEMENT_STATUS } from 'src/@infringements/mutations';
import { IInfringementsData, ITableContainer } from 'src/@infringements/interfaces';
import { hideInfringementColumns } from 'src/utils/tableColumns';
import { useScreenLoadStateContext } from 'src/app/providers/ScreenLoadStateContext';

import { useGetInfringements } from '../hooks/useGetInfringements';
import { LoadMoreBar } from 'src/componentsModern';
import { useInfringementDataTable } from '../reducers';
import {
  OrderByType,
  GetInfringementCandidatesQuery,
  GetInfringementsQuery,
} from 'src/app/gql/__generated__/hooks';
import { InfringementTableDataActionType } from '../reducers/tableData';

export const TableContainer: FC<ITableContainer> = ({ fromCorrelated = false }) => {
  const dispatcher = useContext<any>(InfringementDispatchContext);
  const dataSelectors = useDataSelectors();
  const rolesSelectors = useRolesSelectors();
  const canUpdate = rolesSelectors.hasPermission('Infringement.Update');
  const canReadCandidates = rolesSelectors.hasPermission('InfringementCandidateSummary.Read');
  const { selectedData, areAllRowsSelected, isLoadingMore } = dataSelectors.getDataInformation();

  const [updateInfringementStatus] = useMutation(UPDATE_INFRINGEMENT_STATUS, {
    update(cache, { data: { updateInfringement } }) {
      const id = updateInfringement.id;
      const status = updateInfringement.status;

      dispatcher({
        type: dataActionTypes.INLINE_EDIT_STATUS,
        data: {
          id,
          status,
          candidatesEnabled: state.hasCandidatesEnabled,
        },
      });
    },
  });

  const {
    data,
    loading: tableLoading,
    fetchMore,
    refetch,
    loadingMore: tableLoadingMore,
  } = useGetInfringements(dispatcher);
  const { state, dispatch } = useInfringementDataTable();

  const handleGetMore = (): void => {
    if (!data || !data?.pageInfo?.hasNextPage) {
      console.log('No more data to load');
      return;
    }

    void fetchMore({
      variables: {
        after: data.pageInfo.endCursor,
        first: loadMoreSize,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        if (state.hasCandidatesEnabled) {
          const typedPrev = prev as GetInfringementCandidatesQuery;
          const typedFetchMoreResult = fetchMoreResult as GetInfringementCandidatesQuery;

          return {
            ...typedPrev,
            infringementCandidateSummariesByTimeRange: {
              ...typedPrev.infringementCandidateSummariesByTimeRange,
              items: [
                ...(typedPrev.infringementCandidateSummariesByTimeRange?.items || []),
                ...(typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.items || []),
              ],
              pageInfo:
                typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.pageInfo ||
                typedPrev.infringementCandidateSummariesByTimeRange?.pageInfo,
              totalCount:
                typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.totalCount ||
                typedPrev.infringementCandidateSummariesByTimeRange?.totalCount,
            },
          };
        } else {
          const typedPrev = prev as GetInfringementsQuery;
          const typedFetchMoreResult = fetchMoreResult as GetInfringementsQuery;

          return {
            ...typedPrev,
            infringementSummariesByTimeRange: {
              ...typedPrev.infringementSummariesByTimeRange,
              items: [
                ...(typedPrev.infringementSummariesByTimeRange?.items || []),
                ...(typedFetchMoreResult.infringementSummariesByTimeRange?.items || []),
              ],
              pageInfo:
                typedFetchMoreResult.infringementSummariesByTimeRange?.pageInfo ||
                typedPrev.infringementSummariesByTimeRange?.pageInfo,
              totalCount:
                typedFetchMoreResult.infringementSummariesByTimeRange?.totalCount ||
                typedPrev.infringementSummariesByTimeRange?.totalCount,
            },
          };
        }
      },
    });
  };

  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    globals: { '12HourFormat': twelveHourFormat },
    grid: { resultSize, loadMoreSize },
    infringements: {
      grid: { statusColors, hiddenColumns },
    },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const translationStrings = languageSelectors.getLanguage();
  const {
    components: {
      buttons: { loadMore: loadMoreText },
      labels: {
        table: { endTable },
      },
      hints: { tryChangingFiltersOrDate },
      lists: {
        aircraftCategories,
        candidates,
        operationTypes,
        ruleTypes,
        severities,
        infringementStatuses,
      },
    },
    screens: {
      infringements: {
        errors: { noDataFound },
      },
    },
  } = translationStrings;

  const [translationDataList, setTranslationDataList] = useState<object[]>([]);
  useEffect(() => {
    setTranslationDataList(
      capitalizeObjectKeys({
        ...aircraftCategories,
        ...operationTypes,
        ...ruleTypes,
        ...severities,
        ...infringementStatuses,
        ...candidates,
      })
    );
  }, [aircraftCategories, operationTypes, ruleTypes, severities, infringementStatuses, candidates]);
  const loading = tableLoading || isLoadingMore;

  // Always 'show' all columns, then hide the ones we don't need.
  const columns = [
    'displayRuleName',
    'displayTime',
    'displayInfringement',
    'displaySeverity',
    'acid',
    'displayCategory',
    'aircraftType',
    'displayStatus',
    'displayCandidates',
  ];

  const hiddenColumnsNonNull = hiddenColumns ? hiddenColumns : [];
  const showSeverity = !hiddenColumnsNonNull.includes('displaySeverity');
  const showCandidates =
    !hiddenColumnsNonNull.includes('displayCandidates') && !fromCorrelated && canReadCandidates;
  const showStatus = !hiddenColumnsNonNull.includes('displayStatus');
  // column types are used to add class-names to each column (used only for styling)
  // TODO: this logic needs to be handled in the design system, not here. We'll refactor when the table needs to be modified.
  const columnTypes = {
    rule: {
      title: 'rule',
      abbreviation: '',
    },
    acid: {
      title: 'record-title',
      abbreviation: '',
    },
    displayRunwayName: {
      title: 'runway',
      abbreviation: 'Rwy',
    },
    displayCategory: {
      title: 'aircraft',
      abbreviation: 'Categ',
    },
    displayTime: {
      title: 'time',
      abbreviation: '',
    },
    displayStatus: {
      title: 'status',
      abbreviation: '',
    },
    displayInfringement: {
      title: 'infringement',
      abbreviation: '',
      grow: showSeverity ? null : '2',
    },
    displaySeverity: {
      title: 'severity',
      abbreviation: '',
      grow: '2',
    },
    aircraftType: {
      title: 'model',
      abbreviation: '',
      grow: '3',
    },
    displayCandidates: {
      title: 'candidates',
      abbreviation: '',
    },
  };

  const handleInlineDropdown = useCallback((infringementId, newStatus, infringementType) => {
    updateInfringementStatus({
      variables: { id: infringementId, status: newStatus },
      optimisticResponse: {
        __typename: infringementType,
        updateInfringement: {
          id: infringementId,
          status: newStatus,
          __typename: infringementType,
        },
      },
    }).then(() => {
      refetch();
    });
  }, []);

  const onSelectRow = id => {
    selectRow(id, dispatcher);
  };

  hideInfringementColumns({
    showSeverity,
    showCandidates,
    showStatus,
  });

  const [statusItems, setStatusItems] = useState<any[]>([]);
  const filtersSelectors = useFilterDataSelectors();
  const infringementFilterData = filtersSelectors.getInfringementFilterData();

  useEffect(() => {
    const items: any[] = [];
    const statusOptions = infringementFilterData.statuses;
    statusOptions.map((key: string) => {
      const option = {
        key,
        label: translationDataList[key] ? translationDataList[key] : key,
        icon: <Icons iconName="ic-ui-circle" fill={statusColors[key]} />,
      };
      items.push(option);
    });
    setStatusItems(items);
  }, [translationDataList]);

  // Candidates
  const handleCandidatesChange = (bool: boolean) => {
    dispatch({
      type: InfringementTableDataActionType.TOGGLE_CANDIDATES,
      payload: bool,
    });
  };

  const [displayData, setDisplayData] = useState<IInfringementsData[]>([]);
  const { itemsIds, hasNextPage, endCursor, dateRange } = dataSelectors.getNavigationData();
  const ruleInfo = { itemsIds, hasNextPage, endCursor, dateRange };
  useEffect(() => {
    setDisplayData(
      formatInfringementData(
        tableLoading && !tableLoadingMore ? [] : data?.items || [],
        translationDataList,
        statusItems,
        handleInlineDropdown,
        ruleInfo,
        canUpdate,
        state.hasCandidatesEnabled,
        twelveHourFormat
      )
    );
  }, [
    data,
    statusItems,
    translationDataList,
    handleInlineDropdown,
    tableLoading,
    tableLoadingMore,
  ]);

  // Connect map load state to table load state
  const { screenLoadStates, setScreenLoadStates } = useScreenLoadStateContext();
  const { mapLoadState, tableLoadState } = screenLoadStates;

  useEffect(() => {
    setScreenLoadStates({ type: 'SET_TABLE_LOAD_STATE', payload: tableLoading });
  }, [tableLoading]);

  // Custom override to get sort icons working in header component with new state
  const customSortSelectors = {
    getSortString: () => {
      const sortOb = state.variables.sort;
      return JSON.stringify(sortOb, null, 2);
    },
    getIconProps: (sortName: string) => {
      const { sort } = state?.variables;

      if (sortName === 'time' && !Array.isArray(sort)) {
        // do not show icon for time when it is implicit
        return false;
      }
      const sortArr = (sort as OrderByType[]) || [];
      if (sortArr.length > 1) {
        return false;
      }
      return sortArr.find(sortString => sortString.field && sortString.field === sortName) || false;
    },
  };

  return (
    <>
      <Table
        className={`infringements-table infringements-table--regular`}
        loading={tableLoadState || mapLoadState || isLoadingMore}
        rowHeaders={formatHeaders(
          resultSize,
          loading,
          dispatcher,
          customSortSelectors,
          state.hasCandidatesEnabled,
          handleCandidatesChange,
          !!state.variables.filter.ruleNames,
          translationStrings
        )}
        data={displayData}
        columns={columns}
        columnTypes={columnTypes}
        selectedData={selectedData}
        areAllRowsSelected={areAllRowsSelected}
        gridID="infringements"
        onSelectRow={onSelectRow}
        hasEnded={!data?.pageInfo?.hasNextPage}
        languageData={{ noDataTitle: noDataFound, noDataText: tryChangingFiltersOrDate, endTable }}
      />
      {!!data?.pageInfo?.hasNextPage && (
        <LoadMoreBar isLoading={tableLoadingMore} onClick={handleGetMore} disabled={tableLoading}>
          {loadMoreText}
        </LoadMoreBar>
      )}
    </>
  );
};
